import { supportApi } from 'services'

/**
 * What a support attachment may be, how big, how many, and how a batch of them gets
 * uploaded — for EVERY surface that attaches files to a request.
 *
 * ## Why this is a module and not three constants in the intake
 *
 * These lived in `SupportRequestForm`, which was correct while the intake was the only
 * thing that could attach a file. Replies can now attach too (both the brand thread and
 * the internal staff reply), which makes the reply composer the SECOND hand-roll — and
 * §36 says the second is a shared unit, not a copy.
 *
 * The copy would have drifted in a way nobody would catch from a diff: the accept map,
 * the size cap and the count cap each mirror a server-side constant, so a change to the
 * API's limits would have had to be made in two client places, and the one that was
 * missed would silently refuse files the server would have taken (or, worse, offer files
 * the server rejects after the user has waited for the upload).
 */

/**
 * The formats a support attachment may be.
 *
 * Images and PDF cover the overwhelming majority (a screenshot of the broken screen, or
 * the invoice/export someone is asking about); mp4/mov/webm cover the screen recording,
 * which is frequently the most useful thing a reporter can send.
 *
 * SVG is excluded on purpose even though it is an image: it can carry script, and these
 * files are served from a public origin and then handed to a support agent to click.
 * Video is NOT the same risk — a media container is decoded, not executed — which is why
 * it can be admitted where SVG cannot.
 */
export const ATTACHMENT_ACCEPT = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  // Screen recordings. Often a better bug report than a still — they carry the sequence, the
  // timing, and the thing that only happens on the third click. .mov/.mp4 are what Apple's own
  // capture tools and every phone produce; .webm is what browser capture extensions produce.
  'video/mp4': ['.mp4', '.m4v'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
}

/**
 * Matches SUPPORT_ATTACHMENT_MAX_SIZE_MB on the API. Advisory here — the picker refuses early as
 * a courtesy; multer's `limits.fileSize` is the control.
 *
 * 50, not 10, because video is accepted: a 30-second retina screen recording is routinely
 * 5–25 MB, so a 10 MB cap would admit the format and then reject most real files.
 */
export const ATTACHMENT_MAX_SIZE_MB = 50

/**
 * Matches SUPPORT_ATTACHMENT_MAX_COUNT on the API, and like the size above it is advisory:
 * the picker refuses the sixth file early as a courtesy, while the server's own `.slice()`
 * is the control. Restated rather than imported because `apps/platform` does not depend on
 * `apps/api`; if these two numbers ever disagree the server wins, silently and correctly.
 */
export const ATTACHMENT_MAX_COUNT = 5

/** What an upload batch produced: the URLs that landed, and the names that did not. */
export interface AttachmentUploadResult {
  urls: string[]
  /** File names that failed to upload. Empty on a clean batch. */
  failed: string[]
}

/**
 * Upload a batch of held files, on the way to submitting the thing they belong to.
 *
 * ## On submit, not on select
 *
 * Deliberately called from the submit handler rather than when the file is chosen.
 * Uploading on select writes a blob to the bucket for every abandoned draft, owned by
 * no row, and there is no reaper job in this repo to sweep them. On-submit cannot
 * orphan: nothing is stored unless a request or a reply is about to exist to point at
 * it. The cost is a short extra leg before the write, which the caller's button says
 * out loud ("Uploading…").
 *
 * ## Concurrent, and `allSettled` rather than `all`
 *
 * Concurrent because these are independent PUTs and a serial loop makes the wait the
 * SUM of them — what turns "a second" into five.
 *
 * `allSettled` because PARTIAL SUCCESS IS THE POINT. `Promise.all` rejects on the first
 * failure and abandons the URLs that already succeeded, so one bad file would strip
 * every attachment off the message. Here each file is judged on its own.
 *
 * The caller decides what to SAY about `failed`, because the sentence differs by
 * surface ("your request was sent without it" vs. "your reply was sent without it") —
 * but no caller may let a failed upload eat the text the user wrote. The files are the
 * optional part; the paragraph is the whole point.
 */
export const uploadAttachments = async (files: File[]): Promise<AttachmentUploadResult> => {
  const urls: string[] = []
  const failed: string[] = []
  if (files.length === 0) return { urls, failed }

  const settled = await Promise.allSettled(files.map(file => supportApi.uploadAttachment(file)))

  settled.forEach((result, i) => {
    if (result.status === 'fulfilled') urls.push(result.value.url)
    else failed.push(files[i].name)
  })

  return { urls, failed }
}
